import React, { useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { OptionsType, OptionType } from '@lendi-ui/dropdown';
import { Body } from '@lendi-ui/typography';

import { FIELD_PATHS, FIELD_VALUES, LOCATION_TYPE, YES_NO_OPTIONS } from '@funnels/constants';
import { ComplianceEntityIndex } from '@funnels/types';
import {
  CheckboxField,
  DropdownField,
  FunnelGrid,
  FunnelGridUnit,
  RadioButtonsField,
  TextAreaField,
} from '@funnels/ui-components';

import { MarginLeftWrappered, MarginTopWrappered, RadioButtonsFieldWrappered } from './index.style';

interface ComplianceProps extends ComplianceEntityIndex {
  borrowers: OptionsType<OptionType>;
  isOptional: boolean;
}

export const CustomerDiscussionSummaryField = ({ complianceIndex, isOptional }: Omit<ComplianceProps, 'borrowers'>) => {
  return (
    <FunnelGrid mt="md" mb="lg" data-component="CustomerDiscussionSummaryField-container">
      <FunnelGridUnit size={1 / 2}>
        Please provide commentary regarding what discussion was held with the customer and how the relevant loan term
        was selected.
      </FunnelGridUnit>
      <FunnelGridUnit size={1 / 2}>
        <TextAreaField
          isOptional={isOptional}
          name={FIELD_PATHS.BROKER_INTERVIEW_LOAN_TEARM_SOUGHT_DESCRIPTION(complianceIndex)}
          isFullWidth
          resize="vertical"
        />
      </FunnelGridUnit>
    </FunnelGrid>
  );
};

export const RequirementAndObjectSummaryField = ({
  complianceIndex,
  isOptional,
}: Omit<ComplianceProps, 'borrowers'>) => {
  return (
    <FunnelGrid mt="md" mb="lg" data-component="RequirementAndObjectSummaryField-container">
      <FunnelGridUnit size={1 / 2}>
        <Body>
          Product selection needs to take place having regard to the outcome of the requirements and objectives'
          enquiries set out above.
        </Body>
        <Body mt="sm">
          Please provide a concise narrative summary of the applicant(s)'s requirements and objectives and why the
          particular type of loan product was chosen to meet those requirements and objectives.
        </Body>
      </FunnelGridUnit>
      <FunnelGridUnit size={1 / 2}>
        <TextAreaField
          isOptional={isOptional}
          name={FIELD_PATHS.BROKER_INTERVIEW_PRODUCT_SELECTION(complianceIndex)}
          isFullWidth
          resize="vertical"
        />
      </FunnelGridUnit>
    </FunnelGrid>
  );
};

export const UnderstandEnglishField = ({ complianceIndex, isOptional }: Omit<ComplianceProps, 'borrowers'>) => {
  const { getFieldState } = useForm();
  return (
    <FunnelGrid mt="md" mb="lg" data-component="UnderstandEnglishField-container">
      <FunnelGridUnit size={1 / 2}>
        <Body>Do all applicants appear to clearly understand English?</Body>
      </FunnelGridUnit>
      <FunnelGridUnit size={1 / 2}>
        <RadioButtonsFieldWrappered>
          <RadioButtonsField
            name={FIELD_PATHS.BROKER_INTERVIEW_ALL_APPLICANTS_UNDERSTAND_ENGLISH(complianceIndex)}
            size="sm"
            radios={YES_NO_OPTIONS}
            noVerticalSpace
            selectedValue={
              getFieldState(FIELD_PATHS.BROKER_INTERVIEW_ALL_APPLICANTS_UNDERSTAND_ENGLISH(complianceIndex))?.value
            }
            isOptional={isOptional}
            isBoxed={false}
          />
        </RadioButtonsFieldWrappered>
      </FunnelGridUnit>
    </FunnelGrid>
  );
};

export const InterviewLocationField = ({ complianceIndex, isOptional }: Omit<ComplianceProps, 'borrowers'>) => {
  const [selection, setSelection] = useState('');
  const { change, getFieldState } = useForm();
  useEffect(() => {
    const value = getFieldState(FIELD_PATHS.BROKER_INTERVIEW_LOCATION_TYPE(complianceIndex))?.value;
    setSelection(value);
  }, []);
  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSelection(value);
    if (value === LOCATION_TYPE.LendiApprovedPhoneInterview) {
      change(FIELD_PATHS.BROKER_INTERVIEW_LOCATION(complianceIndex), undefined);
    }
  };
  return (
    <FunnelGrid mt="md" mb="lg" data-component="InterviewLocationField-container">
      <FunnelGridUnit size={1 / 2}>
        <Body>Where did I interview the customer?</Body>
      </FunnelGridUnit>
      <FunnelGridUnit size={1 / 2}>
        <RadioButtonsFieldWrappered>
          <RadioButtonsField
            name={FIELD_PATHS.BROKER_INTERVIEW_LOCATION_TYPE(complianceIndex)}
            size="sm"
            radios={[
              { label: 'Lendi Group approved interview call', value: LOCATION_TYPE.LendiApprovedPhoneInterview },
              { label: 'Face to face', value: LOCATION_TYPE.Other },
            ]}
            selectedValue={getFieldState(FIELD_PATHS.BROKER_INTERVIEW_LOCATION_TYPE(complianceIndex))?.value}
            onChange={onSelect}
            noVerticalSpace
            isOptional={isOptional}
            isBoxed={false}
          />
        </RadioButtonsFieldWrappered>
        <MarginTopWrappered>
          <TextAreaField
            label="Provide location description"
            isOptional={selection !== LOCATION_TYPE.Other}
            isDisabled={selection !== LOCATION_TYPE.Other}
            disableFailMessage={selection !== LOCATION_TYPE.Other}
            rows={1}
            maxRows={2}
            name={FIELD_PATHS.BROKER_INTERVIEW_LOCATION(complianceIndex)}
          />
        </MarginTopWrappered>
      </FunnelGridUnit>
    </FunnelGrid>
  );
};

export const InterviewInpersonField = ({
  complianceIndex,
  isOptional,
  borrowers,
}: Omit<ComplianceProps, 'brokerInterviewsContract'>) => {
  const [selection, setSelection] = useState('');
  const { change, getFieldState } = useForm();
  useEffect(() => {
    const value = getFieldState(FIELD_PATHS.BROKER_INTERVIEW_ALL_APPLICANTS_PRESENT(complianceIndex))?.value;
    //if form has value (true, false, 'TRUE', 'YES', 'FALSE' etc...), set value via form value, truthy -> 'TRUE', falsy -> 'FALSE'
    //if no form value (null, undefined, ...etc) set value to 'TRUE'
    const initialValue = value === false ? FIELD_VALUES.FALSE : FIELD_VALUES.TRUE;
    setSelection(initialValue);
  }, []);
  const onRadioSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSelection(value);
    if (value === FIELD_VALUES.TRUE) {
      //empty array will pass the validation. undefined will not
      //undefined can not make the dropdown empty, need to empty array first
      change(FIELD_PATHS.BROKER_INTERVIEW_ATTENDEEES(complianceIndex), []);
      change(FIELD_PATHS.BROKER_INTERVIEW_ATTENDEEES(complianceIndex), undefined);
    }
  };
  const onDropdownSelect = (v: Array<string>) => {
    const valueInForm = getFieldState(FIELD_PATHS.BROKER_INTERVIEW_ATTENDEEES(complianceIndex))?.value;
    if (!valueInForm?.length) return;
    if (v.length >= borrowers.length) {
      setSelection(FIELD_VALUES.TRUE);
      change(FIELD_PATHS.BROKER_INTERVIEW_ALL_APPLICANTS_PRESENT(complianceIndex), true);
      setTimeout(() => {
        //empty array will pass the validation. undefined will not
        //undefined can not make the dropdown empty, need to empty array first
        change(FIELD_PATHS.BROKER_INTERVIEW_ATTENDEEES(complianceIndex), []);
        change(FIELD_PATHS.BROKER_INTERVIEW_ATTENDEEES(complianceIndex), undefined);
      }, 0);
    }
  };
  return (
    <FunnelGrid mt="md" mb="lg" data-component="InterviewInpersonField-container">
      <FunnelGridUnit size={1 / 2}>
        <Body>Were all borrowers interviewed in person?</Body>
      </FunnelGridUnit>
      <FunnelGridUnit size={1 / 2}>
        <RadioButtonsFieldWrappered>
          <RadioButtonsField
            name={FIELD_PATHS.BROKER_INTERVIEW_ALL_APPLICANTS_PRESENT(complianceIndex)}
            size="sm"
            radios={YES_NO_OPTIONS}
            selectedValue={getFieldState(FIELD_PATHS.BROKER_INTERVIEW_ALL_APPLICANTS_PRESENT(complianceIndex))?.value}
            noVerticalSpace
            isOptional={isOptional}
            onChange={onRadioSelect}
            isBoxed={false}
          />
        </RadioButtonsFieldWrappered>
        <MarginTopWrappered>
          <DropdownField
            label="Which borrower did you interview in person?"
            name={FIELD_PATHS.BROKER_INTERVIEW_ATTENDEEES(complianceIndex)}
            onChange={onDropdownSelect}
            key={`${FIELD_PATHS.BROKER_INTERVIEW_ATTENDEEES(complianceIndex)}-${selection !== FIELD_VALUES.FALSE}`}
            isMultiple
            isOptional
            isDisabled={selection !== FIELD_VALUES.FALSE}
            options={borrowers}
          />
        </MarginTopWrappered>
      </FunnelGridUnit>
    </FunnelGrid>
  );
};

export const UnderstandingCheckField = ({ complianceIndex }: ComplianceEntityIndex) => {
  return (
    <FunnelGrid mt="md" mb="lg" data-component="UnderstandingCheckField-container">
      <FunnelGridUnit size={1 / 2}>
        <Body>
          Where loan purpose includes Refinance, Debt Consolidation and/or Purchase a motor vehicle, boat, trailer, etc,
          the following declaration would need to be made by the broker and acknowledged by the applicant(s).
        </Body>
        <ul>
          <li>
            <Body mb="sm">
              I have discussed that the goods being financed / refinanced may not hold their value for as long as the
              remaining term of the loan and so the applicant(s) may be paying for those goods beyond their useful
              lifespan and value. Additionally, I have discussed that the applicant(s) may not be in a position to
              finance the replacement of those goods if the term exceeds the lifespan of the original goods.
            </Body>
          </li>
          <li>
            <Body mb="sm">
              I have discussed with the applicant(s) that they may end up paying more interest compared to taking the
              loan over a shorter term more in line with the useful life of the goods.
            </Body>
          </li>
          <li>
            <Body mb="sm">
              I have discussed with the applicant the option of a separate loan with a shorter term for the part of the
              loan required for the purpose of the goods.
            </Body>
          </li>
        </ul>
      </FunnelGridUnit>
      <FunnelGridUnit size={1 / 2}>
        <MarginLeftWrappered>
          <CheckboxField
            size="sm"
            checkboxLabel="The applicant(s) appeared to understand and wants to proceed"
            name={FIELD_PATHS.BROKER_INTERVIEW_REFINANCING_AND_DEBT_CONSOLIDATION_MEETS_OBJECTIVES(complianceIndex)}
          />
        </MarginLeftWrappered>
      </FunnelGridUnit>
    </FunnelGrid>
  );
};
